/**
 * Completion content for the "confirm-services" activity.
 * Per-ServiceRequest member response capture that gates forward progress.
 * Creates a Communication (progress log) to record the member conversation.
 * @agentic connections
 */

import {
  Stack,
  Text,
  Textarea,
  Alert,
  Select,
  Skeleton,
  Paper,
  Group,
  Badge,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle } from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getDisplayString } from "@medplum/core";
import { useMedplum, useMedplumProfile } from "@medplum/react";
import type {
  CarePlan,
  Patient,
  Practitioner,
  PractitionerRole,
  ServiceRequest,
} from "@medplum/fhirtypes";
import { COMMUNICATION_MEDIUM_SELECT_OPTIONS } from "@benchmark/core/constants";
import { CardSelect } from "@benchmark/ui/components/atomic";
import { FormStep, SteppedForm } from "@benchmark/ui/components/atomic";
import { NextTaskOwnerSection } from "@/components/shared/forms/NextTaskOwnerSection";
import { CareTimeActivityDurationField } from "@/components/domain/care-time/CareTimeActivityDurationField";
import { useTeeCareTimeForTask } from "@/hooks/domain/useTeeCareTimeForEncounter";
import { useCarePlanServiceRequests } from "@/hooks/domain/useCarePlanServiceRequests";
import { usePatientResource } from "@/hooks/shared/useFHIRResource";
import { findBenchmarkPractitionerRole } from "@benchmark/core/services";
import { logActivityProgress } from "@benchmark/core/actions";
import { getResourceIdFromReference } from "@benchmark/core/utils";
import {
  showSuccessNotification,
  showWarningNotification,
} from "@benchmark/ui/utils";
import { updateCarePlanActivity } from "@benchmark/core/actions";
import { snoozeTask } from "@benchmark/core/actions";
import { PriorReviewsSection } from "./PriorReviewsSection";
import type { CompletionContentProps } from "./DefaultCompletionContent";
import {
  getCarePlanIdFromTask,
  getConditionDisplays,
  getDecisionNotes,
  getLatestMemberAcceptanceNote,
  getLatestSelectionNote,
  getPreferencesText,
  getSelectedServiceDisplay,
  getServiceRequestVisitModeDisplay,
  getServiceTypeDisplay,
  hasSelectedService,
} from "@benchmark/core/resolvers";
import { CONFIG } from "@/app/config";

const CONFIRM_CONTACT_METHOD_OPTIONS =
  COMMUNICATION_MEDIUM_SELECT_OPTIONS.filter(
    (o) => o.value === "phone" || o.value === "text",
  );

const MEMBER_SR_DECISIONS = [
  { value: "accepted", label: "Member accepted" },
  { value: "declined", label: "Member declined" },
  { value: "wants-changes", label: "Member wants changes" },
  { value: "needs-time", label: "Needs more time" },
] as const;

/** Per-SR member decision state. */
type SrMemberDecision = {
  decision: string | null;
  feedback: string;
};

/**
 * Card for a single ServiceRequest with read-only details and a per-SR member decision.
 * @agentic connections
 */
const MemberConfirmationServiceCard = ({
  sr,
  index,
  totalCount,
  srDecision,
  onDecisionChange,
  onFeedbackChange,
}: {
  sr: ServiceRequest;
  index: number;
  totalCount: number;
  srDecision: SrMemberDecision;
  onDecisionChange: (value: string | null) => void;
  onFeedbackChange: (value: string) => void;
}) => {
  const serviceTypeDisplay = getServiceTypeDisplay(sr);
  const conditions = getConditionDisplays(sr);
  const preferences = getPreferencesText(sr);
  const modeDisplay = getServiceRequestVisitModeDisplay(sr);
  const selectionNote = getLatestSelectionNote(sr);
  const decisionHistory = getDecisionNotes(sr);

  const badgeLabel =
    totalCount > 1
      ? `${index + 1} \u00B7 ${serviceTypeDisplay.toUpperCase()}`
      : serviceTypeDisplay.toUpperCase();

  const decisionColor =
    srDecision.decision === "accepted"
      ? "green"
      : srDecision.decision === "declined" ||
          srDecision.decision === "wants-changes"
        ? "red"
        : srDecision.decision === "needs-time"
          ? "orange"
          : "gray";

  return (
    <Paper
      p="md"
      withBorder
      style={
        srDecision.decision
          ? {
              borderColor: `var(--mantine-color-${decisionColor}-4)`,
              borderWidth: 2,
            }
          : undefined
      }
    >
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Badge variant="filled" color="purple" size="lg" radius="sm">
            {badgeLabel}
          </Badge>
          {srDecision.decision && (
            <Badge variant="light" color={decisionColor} size="sm">
              {MEMBER_SR_DECISIONS.find((o) => o.value === srDecision.decision)
                ?.label ?? srDecision.decision}
            </Badge>
          )}
        </Group>

        <Group gap={4} wrap="wrap">
          {conditions.map((c) => (
            <Badge key={c} size="xs" variant="light" color="purple">
              {c}
            </Badge>
          ))}
        </Group>

        {preferences && (
          <Text size="sm" c="purple.7" fw={500}>
            Member Preferences: {preferences}
          </Text>
        )}

        <Stack gap={4}>
          <Group gap="xs" wrap="nowrap">
            <Text size="sm" fw={500} style={{ flexShrink: 0 }}>
              Selected Service:
            </Text>
            <Text size="sm">{getSelectedServiceDisplay(sr)}</Text>
          </Group>

          {modeDisplay !== "—" && (
            <Group gap="xs" wrap="nowrap">
              <Text size="sm" fw={500} style={{ flexShrink: 0 }}>
                Service Mode:
              </Text>
              <Text size="sm">{modeDisplay}</Text>
            </Group>
          )}

          {selectionNote?.websiteUrls &&
            selectionNote.websiteUrls.length > 0 && (
              <Group gap="xs" wrap="nowrap" align="flex-start">
                <Text size="sm" fw={500} style={{ flexShrink: 0 }}>
                  {selectionNote.websiteUrls.length === 1
                    ? "Website:"
                    : "Websites:"}
                </Text>
                <Stack gap={2}>
                  {selectionNote.websiteUrls.map((url, i) => (
                    <Text key={i} size="sm">
                      {url}
                    </Text>
                  ))}
                </Stack>
              </Group>
            )}

          {selectionNote?.rationale && (
            <Group gap="xs" wrap="nowrap" align="flex-start">
              <Text size="sm" fw={500} style={{ flexShrink: 0 }}>
                Rationale:
              </Text>
              <Text size="sm">{selectionNote.rationale}</Text>
            </Group>
          )}
        </Stack>

        <PriorReviewsSection decisionHistory={decisionHistory} />

        <Select
          label="Member Response"
          placeholder="Record member's decision..."
          required
          data={MEMBER_SR_DECISIONS}
          value={srDecision.decision}
          onChange={onDecisionChange}
          size="sm"
        />

        {srDecision.decision && (
          <Textarea
            label={
              srDecision.decision === "accepted"
                ? "Member Notes (optional)"
                : "Member Feedback"
            }
            placeholder={
              srDecision.decision === "accepted"
                ? "E.g. member's preferred appointment times, questions asked..."
                : "Record member's reasoning for this service..."
            }
            value={srDecision.feedback}
            onChange={(e) => onFeedbackChange(e.currentTarget.value)}
            autosize
            minRows={2}
            maxRows={3}
            size="sm"
          />
        )}
      </Stack>
    </Paper>
  );
};

/**
 * Completion form for confirm-services: per-SR member decisions + assignment.
 * @agentic connections
 */
export const ConfirmServicesCompletionContent = ({
  task,
  onComplete,
  onDone,
  onCancel,
  onDirtyChange,
  nextActivityLabel,
  nextActivityDefaultOwner,
  nextActivityAppliedRule,
  nextTaskOwnerOverride,
  onNextTaskOwnerOverrideChange,
  isResolvingNextTaskOwner = false,
}: CompletionContentProps) => {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const patientId = getResourceIdFromReference(task.for?.reference, "Patient");
  const { data: patientResourceRaw } = usePatientResource(patientId);
  const patient = patientResourceRaw as Patient | undefined;
  const [practitionerRole, setPractitionerRole] = useState<
    PractitionerRole | undefined
  >();

  useEffect(() => {
    if (!profile?.id) return;
    findBenchmarkPractitionerRole(medplum, {
      reference: `Practitioner/${profile.id}`,
      benchmarkOrganizationId: CONFIG.VITE_BENCHMARK_ORGANIZATION_ID,
    })
      .then(setPractitionerRole)
      .catch(() => setPractitionerRole(undefined));
  }, [medplum, profile?.id]);

  const form = useForm<{
    decisions: Record<string, SrMemberDecision>;
    contactMethod: string;
  }>({
    initialValues: {
      decisions: {},
      contactMethod: "",
    },
  });
  const carePlanId = getCarePlanIdFromTask(task);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [careMinutes, setCareMinutes] = useState<number | undefined>(undefined);
  const teeForTask = useTeeCareTimeForTask();

  const {
    serviceRequests,
    isLoading: srLoading,
    error: srError,
  } = useCarePlanServiceRequests(carePlanId);

  const selectedServiceRequests = useMemo(
    () => serviceRequests.filter(hasSelectedService),
    [serviceRequests],
  );

  useEffect(() => {
    if (selectedServiceRequests.length === 0) return;
    const prev = form.values.decisions;
    let changed = false;
    const next = { ...prev };
    for (const sr of selectedServiceRequests) {
      if (sr.id && !(sr.id in next)) {
        const priorAcceptance = getLatestMemberAcceptanceNote(sr);
        next[sr.id] = priorAcceptance
          ? { decision: "accepted", feedback: "" }
          : { decision: null, feedback: "" };
        changed = true;
      }
    }
    if (changed) {
      form.setFieldValue("decisions", next);
    }
  }, [selectedServiceRequests, form]);

  const decisions = form.values.decisions;

  const allDecided = useMemo(
    () =>
      selectedServiceRequests.length > 0 &&
      selectedServiceRequests.every(
        (sr) => sr.id && decisions[sr.id]?.decision,
      ),
    [selectedServiceRequests, decisions],
  );

  const hasAnyRejection = useMemo(
    () =>
      Object.values(decisions).some(
        (d) => d.decision === "declined" || d.decision === "wants-changes",
      ),
    [decisions],
  );

  const hasAnyNeedsTime = useMemo(
    () => Object.values(decisions).some((d) => d.decision === "needs-time"),
    [decisions],
  );

  const allAccepted = useMemo(
    () =>
      allDecided &&
      Object.values(decisions).every((d) => d.decision === "accepted"),
    [allDecided, decisions],
  );

  const steps = useMemo(
    () => [
      {
        id: "details",
        title: "Details",
        subtitle: "Record member response for each service",
        validate: () => {
          if (!allDecided) {
            return { decisions: "A response is required for every service" };
          }
          return null;
        },
      },
      ...(allAccepted
        ? [
            {
              id: "assignment",
              title: "Assignment",
              subtitle: "Review next task assignment",
            },
          ]
        : []),
    ],
    [allDecided, allAccepted],
  );

  const resolveOriginalSelector = useCallback(
    async (
      activityCode: string,
    ): Promise<{ ref: string; display: string } | undefined> => {
      if (!carePlanId) return undefined;
      const cp = await medplum.readResource("CarePlan", carePlanId);
      for (const a of cp.activity ?? []) {
        const ref = a.reference?.reference;
        if (!ref?.startsWith("Task/")) continue;
        const linkedTask = await medplum.readResource(
          "Task",
          ref.replace("Task/", ""),
        );
        if (
          linkedTask.code?.coding?.[0]?.code === activityCode &&
          linkedTask.owner?.reference
        ) {
          const ownerRef = linkedTask.owner.reference.replace(
            "PractitionerRole/",
            "",
          );
          return {
            ref: ownerRef,
            display: linkedTask.owner.display || "Prior Selector",
          };
        }
      }
      return undefined;
    },
    [medplum, carePlanId],
  );

  const restartToSelectServices = useCallback(
    async (statusReason: string) => {
      if (!carePlanId) throw new Error("Missing care plan reference");
      let carePlan: CarePlan = await medplum.readResource(
        "CarePlan",
        carePlanId,
      );

      const currentCode = task.code?.coding?.[0]?.code;
      if (!currentCode)
        throw new Error("Cannot determine current activity code");

      carePlan = await updateCarePlanActivity(medplum, {
        carePlan,
        activityCode: currentCode,
        newStatus: "draft",
        statusReason,
      });

      const intermediateCodes = ["offer-services", "approve-selected-services"];
      for (const code of intermediateCodes) {
        try {
          carePlan = await updateCarePlanActivity(medplum, {
            carePlan,
            activityCode: code,
            newStatus: "draft",
            statusReason: "Reset: member requested changes",
          });
        } catch {
          // approve-selected-services is as-needed; may not exist
        }
      }

      const originalSelector = await resolveOriginalSelector("select-services");

      await updateCarePlanActivity(medplum, {
        carePlan,
        activityCode: "select-services",
        newStatus: "ready",
        statusReason,
        ownerOverride: originalSelector,
        dueDateOverride: new Date().toISOString().split("T")[0],
      });
    },
    [medplum, carePlanId, task, resolveOriginalSelector],
  );

  const handleSubmit = useCallback(async () => {
    if (!allDecided) return;

    setSubmitting(true);
    setError(null);

    try {
      const reviewerDisplay = profile
        ? getDisplayString(profile)
        : "Unknown Clinician";
      const now = new Date().toISOString();

      const summaryParts: string[] = [];
      for (const sr of selectedServiceRequests) {
        if (!sr.id) continue;
        const d = decisions[sr.id];
        if (!d?.decision) continue;
        const serviceLabel = getServiceTypeDisplay(sr);
        const decLabel =
          MEMBER_SR_DECISIONS.find((o) => o.value === d.decision)?.label ??
          d.decision;
        summaryParts.push(`${serviceLabel}: ${decLabel}`);
      }

      if (task.id && patient && profile && practitionerRole) {
        try {
          const feedbackSummary = Object.values(decisions)
            .filter((d) => d.feedback.trim())
            .map((d) => d.feedback.trim())
            .join("; ");
          await logActivityProgress(medplum, {
            task,
            progressData: {
              contactMethod:
                (form.values.contactMethod as "phone" | "text") || undefined,
              outcome: "general-progress",
              outcomeDisplay: `Member Confirmation: ${summaryParts.join("; ")}`,
              notes: feedbackSummary || undefined,
              snoozeBehavior: { type: "none" },
            },
            patient,
            practitioner: profile,
            practitionerRole,
          });
        } catch {
          showWarningNotification(
            "Confirmation interaction could not be logged, but completion will proceed.",
          );
        }
      }

      // Annotate each SR with its per-SR member decision (skip if already accepted with no new feedback)
      await Promise.all(
        selectedServiceRequests
          .filter((sr) => sr.id)
          .map((sr) => {
            const d = decisions[sr.id!];
            if (!d?.decision) return Promise.resolve(sr);

            const priorAcceptance = getLatestMemberAcceptanceNote(sr);
            const isRedundantAcceptance =
              d.decision === "accepted" &&
              priorAcceptance &&
              !d.feedback.trim();
            if (isRedundantAcceptance) return Promise.resolve(sr);

            const notePrefix =
              d.decision === "accepted" ? "Member Accepted" : "Member Response";
            return medplum.updateResource({
              ...sr,
              note: [
                ...(sr.note ?? []),
                {
                  authorString: reviewerDisplay,
                  time: now,
                  text: `${notePrefix}: ${d.feedback.trim() || MEMBER_SR_DECISIONS.find((o) => o.value === d.decision)?.label || d.decision} | Decision: ${d.decision}`,
                },
              ],
            });
          }),
      );

      if (allAccepted) {
        const statusReason = `Member accepted: ${summaryParts.join("; ")}`;
        await onComplete(statusReason);
        await teeForTask({
          task,
          minutes: careMinutes,
          method: "task-completion",
        });
        return;
      }

      if (!hasAnyRejection && hasAnyNeedsTime) {
        const dueDate = new Date();
        dueDate.setTime(dueDate.getTime() + 48 * 60 * 60 * 1000);
        await snoozeTask(medplum, {
          task,
          dueDateIso: dueDate.toISOString(),
        });
        showSuccessNotification(
          "Responses recorded — follow up with member in 48h",
        );
        onDone();
        return;
      }

      // Any rejection → restart to select-services
      const statusReason = `Member declined: ${summaryParts.join("; ")}`;
      await restartToSelectServices(statusReason);
      showSuccessNotification(
        "Member responses recorded — returned for re-selection",
      );
      onDone();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to submit decisions",
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    allDecided,
    allAccepted,
    hasAnyRejection,
    hasAnyNeedsTime,
    decisions,
    form.values,
    onComplete,
    onDone,
    medplum,
    profile,
    task,
    patient,
    practitionerRole,
    selectedServiceRequests,
    restartToSelectServices,
    teeForTask,
    careMinutes,
  ]);

  useEffect(() => {
    const anyDecision = Object.values(form.values.decisions).some(
      (d) => d.decision,
    );
    const anyFeedback = Object.values(form.values.decisions).some(
      (d) => d.feedback.trim().length > 0,
    );
    onDirtyChange?.(
      anyDecision ||
        anyFeedback ||
        !!form.values.contactMethod ||
        !!nextTaskOwnerOverride ||
        !!careMinutes,
    );
  }, [form.values, nextTaskOwnerOverride, careMinutes, onDirtyChange]);

  if (!carePlanId) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        Unable to determine care plan from this task.
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      <SteppedForm
        form={form}
        steps={steps}
        onSubmit={() => handleSubmit()}
        isSubmitting={submitting}
        submitText="Submit Member Responses"
        onCancel={onCancel}
      >
        <FormStep id="details">
          <Stack gap="md">
            <Text size="sm">
              Record the member&apos;s response for each offered referral
              service.
            </Text>

            <Stack gap={4}>
              <Text size="sm" fw={500}>
                Contact Method
              </Text>
              <CardSelect
                data={CONFIRM_CONTACT_METHOD_OPTIONS}
                value={form.values.contactMethod || null}
                onChange={(value) => {
                  const singleValue = Array.isArray(value) ? value[0] : value;
                  form.setFieldValue("contactMethod", singleValue ?? "");
                }}
                columns={2}
                compact
                aria-label="Select contact method"
              />
            </Stack>

            {srLoading && (
              <Stack gap="xs">
                <Skeleton height={16} />
                <Skeleton height={60} />
              </Stack>
            )}

            {srError && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="orange"
                variant="light"
              >
                Failed to load service requests: {srError}
              </Alert>
            )}

            {!srLoading &&
              !srError &&
              selectedServiceRequests.length > 0 &&
              selectedServiceRequests.map((sr, index) => (
                <MemberConfirmationServiceCard
                  key={sr.id}
                  sr={sr}
                  index={index}
                  totalCount={selectedServiceRequests.length}
                  srDecision={
                    form.values.decisions[sr.id!] ?? {
                      decision: null,
                      feedback: "",
                    }
                  }
                  onDecisionChange={(value) => {
                    form.setFieldValue("decisions", {
                      ...form.values.decisions,
                      [sr.id!]: {
                        ...form.values.decisions[sr.id!],
                        decision: value,
                        feedback: form.values.decisions[sr.id!]?.feedback ?? "",
                      },
                    });
                  }}
                  onFeedbackChange={(value) => {
                    form.setFieldValue("decisions", {
                      ...form.values.decisions,
                      [sr.id!]: {
                        ...form.values.decisions[sr.id!],
                        feedback: value,
                      },
                    });
                  }}
                />
              ))}

            {!srLoading &&
              !srError &&
              selectedServiceRequests.length === 0 &&
              serviceRequests.length > 0 && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="orange"
                  variant="light"
                >
                  No services have been selected yet.
                </Alert>
              )}
          </Stack>
        </FormStep>

        {allAccepted && (
          <FormStep id="assignment">
            <Stack gap="md">
              <NextTaskOwnerSection
                nextActivityLabel={nextActivityLabel}
                defaultOwner={nextActivityDefaultOwner}
                appliedRule={nextActivityAppliedRule}
                overrideOwner={nextTaskOwnerOverride}
                onOverrideChange={onNextTaskOwnerOverrideChange ?? (() => {})}
                isResolving={isResolvingNextTaskOwner}
              />
              <CareTimeActivityDurationField
                minutes={careMinutes}
                onMinutesChange={setCareMinutes}
              />
            </Stack>
          </FormStep>
        )}
      </SteppedForm>
    </Stack>
  );
};
